import { useCallback, useEffect } from 'react'
import isEmpty from 'lodash/isEmpty'
import flow from 'lodash/flow'
import some from 'lodash/fp/some'
import values from 'lodash/values'
import { useLoading } from 'hooks/UseLoading'
import { negate } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'

import client from 'api/client'
import { UserClient } from 'api/clients/UserClient'
import { FetchRequestState, RootState } from 'types'
import { useSearchParams } from './useSearchParams'
import {
  authenticateFailure,
  authenticateStart,
  authenticateSuccess,
  dismissBanner,
  setName,
} from 'redux/actions/unregisteredUser'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { reportError } from 'utils/error'
import { UnregisteredUserBannerProps } from 'components/UnregisteredUserBanner/Types'

const LOGIN_TOKEN_PARAM = 'unregistered_login_token'
const EMAIL_PARAM = 'email'

export const useUnregisteredUser = () => {
  const params = useSearchParams()
  const dispatch = useDispatch()
  const state = useSelector((state: RootState) => state.unregisteredUser)
  const token = params.get(LOGIN_TOKEN_PARAM)
  const email = params.get(EMAIL_PARAM)

  /**
   * If unregistered user is not authenticated
   * and the query params to do so are present,
   * this effect will authenticate them.
   */
  useEffect(() => {
    const authenticate = async (email: string, token: string) => {
      dispatch(authenticateStart())
      const res = await UserClient.authenticateUnregisteredUser(email, token)

      if (res.type === SUCCESS_RESPONSE_TYPE) {
        const payload = {
          authHeader: res.headers.authorization,
          user: res.data.unregistered_user,
        }
        dispatch(authenticateSuccess(payload))
      } else {
        dispatch(authenticateFailure())
        reportError(new Error(`Failed to authenticate unregistered user with email ${email}`), {
          severity: 'error',
          metaData: { email, token, responseData: res.data },
        })
      }
    }

    if (email && token && state.status === FetchRequestState.Idle) {
      authenticate(email, token)
    }
  }, [email, token, dispatch, state])

  return { ...state, loading: state.status === FetchRequestState.Loading }
}

export const useUnregisteredUserBanner = (): [boolean, UnregisteredUserBannerProps] => {
  const { bannerDismissed, user } = useUnregisteredUser()
  const dispatch = useDispatch()
  const dismiss = useCallback(() => dispatch(dismissBanner()), [dispatch])

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return [Boolean(user) && !bannerDismissed, { dismiss, user: user! }]
}

interface UpdateUserParams {
  first_name: string
  last_name: string
}

const allPresent = negate(flow([values, some(isEmpty)]))

export const useUpdateUnregisteredUserName = (
  params: UpdateUserParams,
  onSuccess?: () => void,
  onError?: () => void,
) => {
  const dispatch = useDispatch()
  const [update, isSubmitting] = useLoading(client.updateUserProfile)

  const updateUserName = useCallback(async () => {
    if (!allPresent(params)) return

    const response = await update(params)

    if (response.type === SUCCESS_RESPONSE_TYPE) {
      dispatch(setName(params))
      onSuccess?.()
    } else {
      reportError(new Error('useUpdateUnregisteredUser received an error response'), {
        metaData: response.data,
        severity: 'error',
      })
      onError?.()
    }
  }, [update, params, onSuccess, onError, dispatch])

  return {
    updateUserName,
    isSubmitting,
    isValid: allPresent(params),
  }
}
